import React, { useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { VideoTrack } from '@livekit/components-react';
import { motion, AnimatePresence } from 'framer-motion';
import ToastNotification from './ToastNotification';

/**
 * VideoCollaborationPanel - 2025 Multi-Friend Video Grid
 *
 * Design: FaceTime + Discord + Google Meet hybrid
 * - Dynamic grid: 1 friend (full), 2 (side-by-side), 3-4 (2x2)
 * - Screen share spotlight mode
 * - Per-participant speaking indicators
 * - Local video PiP with tap-to-move
 *
 * Props passed from parent (LiveKitVoiceInput) to avoid duplicate useTracks calls.
 */
const VideoCollaborationPanel = ({
  show,
  onClose,
  forceMinimize = false,
  // Tracks passed from parent to prevent flickering
  localParticipant,
  isCameraEnabled,
  videoTracks = [],
  screenShareTracks = [],
  friendParticipants = []
}) => {

  // Panel state
  const [isMinimized, setIsMinimized] = useState(false);
  const [userManuallyExpanded, setUserManuallyExpanded] = useState(false);
  const [pipPosition, setPipPosition] = useState('top-left'); // top-left, top-right, bottom-left, bottom-right

  // 2025 features - Connection quality monitoring
  const [connectionQuality, setConnectionQuality] = useState('good');

  // Toast notification state
  const [toastMessage, setToastMessage] = useState(null);
  const [showToast, setShowToast] = useState(false);

  // Auto-minimize only when forceMinimize becomes true (unless user manually expanded)
  React.useEffect(() => {
    if (forceMinimize && !userManuallyExpanded) {
      setIsMinimized(true);
    }
  }, [forceMinimize, userManuallyExpanded]);

  // Monitor connection quality (Discord-style)
  React.useEffect(() => {
    if (!localParticipant) return;

    const updateQuality = () => {
      const quality = localParticipant.connectionQuality;
      if (quality === 'poor') setConnectionQuality('poor');
      else if (quality === 'lost') setConnectionQuality('bad');
      else setConnectionQuality('good');
    };

    updateQuality();
    localParticipant.on('connectionQualityChanged', updateQuality);
    return () => localParticipant.off('connectionQualityChanged', updateQuality);
  }, [localParticipant]);

  // Get participant's name for display (works for both host and friends)
  const getFriendDisplayName = (participant) => {
    if (!participant) return 'Friend';
    // Use participant.name from LiveKit token (set via .with_name())
    if (participant.name && participant.name !== 'undefined') {
      return participant.name;
    }
    // Fallback: parse identity (format: friend_FirstName_LastName_id for friends)
    if (participant.identity?.includes('friend_')) {
      const identityParts = participant.identity.split('_');
      if (identityParts.length >= 3) {
        const firstName = identityParts[1];
        const lastName = identityParts[2] || '';
        return lastName ? `${firstName} ${lastName}` : firstName;
      }
    }
    // Final fallback: use identity directly (for host who has username as identity)
    if (participant.identity && !participant.identity.startsWith('agent-')) {
      return participant.identity;
    }
    return 'Friend';
  };

  // Get initial for avatar fallback
  const getInitial = (participant) => {
    const name = getFriendDisplayName(participant);
    return name.charAt(0).toUpperCase();
  };

  // Local participant SID for stable references
  const localParticipantSid = localParticipant?.sid;

  // Local video track - memoized to prevent video cutting out on parent re-renders
  const myVideoTrack = useMemo(() => {
    if (!localParticipantSid) return null;
    return videoTracks.find(trackRef =>
      trackRef.participant?.sid === localParticipantSid
    );
  }, [localParticipantSid, videoTracks]);

  // Get video track for a specific participant
  const getParticipantVideoTrack = useCallback((participant) => {
    if (!participant?.sid) return null;
    return videoTracks.find(trackRef =>
      trackRef.participant?.sid === participant.sid
    );
  }, [videoTracks]);

  // Get screen share track for a specific participant
  const getParticipantScreenShare = useCallback((participant) => {
    if (!participant?.sid) return null;
    return screenShareTracks.find(trackRef =>
      trackRef.participant?.sid === participant.sid
    );
  }, [screenShareTracks]);

  // Check if anyone is sharing screen
  const activeScreenShare = useMemo(() => {
    for (const friend of friendParticipants) {
      const screenShare = getParticipantScreenShare(friend);
      if (screenShare) {
        return { participant: friend, track: screenShare };
      }
    }
    return null;
  }, [friendParticipants, getParticipantScreenShare]);

  // Toggle camera for friend collaboration
  const toggleCamera = useCallback(async () => {
    if (!localParticipant) return;

    try {
      await localParticipant.setCameraEnabled(!isCameraEnabled);
    } catch (error) {
      console.error('Camera toggle error:', error);
      if (error.name === 'NotAllowedError') {
        setToastMessage('Camera access denied. Please allow camera access in your browser settings.');
        setShowToast(true);
      }
    }
  }, [localParticipant, isCameraEnabled]);

  // Cycle PiP position on tap (iOS-style)
  const cyclePipPosition = () => {
    const positions = ['top-left', 'top-right', 'bottom-right', 'bottom-left'];
    const currentIndex = positions.indexOf(pipPosition);
    setPipPosition(positions[(currentIndex + 1) % positions.length]);
  };

  // Get PiP style based on position
  const getPipStyle = () => {
    const base = {
      position: 'absolute',
      width: '100px',
      height: '75px',
      borderRadius: '12px',
      overflow: 'hidden',
      background: '#1c1c1e',
      border: '2px solid rgba(66, 144, 158, 0.6)',
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.4)',
      zIndex: 10,
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    };

    switch (pipPosition) {
      case 'top-left': return { ...base, top: '12px', left: '12px' };
      case 'top-right': return { ...base, top: '12px', right: '12px' };
      case 'bottom-right': return { ...base, bottom: '12px', right: '12px' };
      case 'bottom-left': return { ...base, bottom: '12px', left: '12px' };
      default: return { ...base, top: '12px', left: '12px' };
    }
  };

  // Calculate grid layout based on participant count and screen share
  const getGridLayout = () => {
    const count = friendParticipants.length;

    if (activeScreenShare) {
      // Screen share mode: spotlight + small tiles
      return {
        columns: 1,
        spotlightMode: true
      };
    }

    if (count === 1) return { columns: 1, spotlightMode: false };
    if (count === 2) return { columns: 2, spotlightMode: false };
    return { columns: 2, spotlightMode: false }; // 3-4 friends = 2x2 grid
  };

  const gridLayout = getGridLayout();

  // Calculate panel width based on participant count
  const getPanelWidth = () => {
    if (isMinimized) return '280px';
    const count = friendParticipants.length;
    if (activeScreenShare) return '520px'; // Wider for screen share
    if (count >= 3) return '480px'; // Wider for grid
    if (count === 2) return '440px'; // Side by side
    return '380px'; // Single friend
  };

  // Render a single participant tile
  const renderParticipantTile = (participant, isSpotlight = false) => {
    const videoTrack = getParticipantVideoTrack(participant);
    const isSpeaking = participant?.isSpeaking || false;
    const name = getFriendDisplayName(participant);

    const tileStyle = isSpotlight ? {
      width: '100%',
      aspectRatio: '16/9',
      borderRadius: '14px',
      overflow: 'hidden',
      background: '#1a1a1e',
      position: 'relative',
      border: isSpeaking ? '3px solid #10b981' : '3px solid rgba(255, 255, 255, 0.08)',
      boxShadow: isSpeaking
        ? '0 0 0 4px rgba(16, 185, 129, 0.2), 0 4px 16px rgba(0, 0, 0, 0.3)'
        : '0 4px 12px rgba(0, 0, 0, 0.2)',
      transition: 'border-color 0.3s, box-shadow 0.3s'
    } : {
      width: '100%',
      aspectRatio: '4/3',
      borderRadius: '12px',
      overflow: 'hidden',
      background: '#1a1a1e',
      position: 'relative',
      border: isSpeaking ? '3px solid #10b981' : '3px solid rgba(255, 255, 255, 0.08)',
      boxShadow: isSpeaking
        ? '0 0 0 3px rgba(16, 185, 129, 0.25)'
        : 'none',
      transition: 'border-color 0.3s, box-shadow 0.3s'
    };

    return (
      <motion.div
        key={participant.sid}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        style={tileStyle}
      >
        {videoTrack ? (
          <VideoTrack
            key={`video-${videoTrack.publication?.trackSid || participant.sid}`}
            trackRef={videoTrack}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          // Avatar fallback
          <div style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}>
            <div style={{
              width: isSpotlight ? '64px' : '48px',
              height: isSpotlight ? '64px' : '48px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #297A92 0%, #42909E 50%, #6DB4CD 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: isSpotlight ? '28px' : '20px',
              fontWeight: '600',
              color: 'white',
              boxShadow: '0 4px 16px rgba(66, 144, 158, 0.35)'
            }}>
              {getInitial(participant)}
            </div>
          </div>
        )}

        {/* Name overlay */}
        <div style={{
          position: 'absolute',
          bottom: '8px',
          left: '8px',
          right: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px'
        }}>
          {/* Speaking indicator dot */}
          {isSpeaking && (
            <motion.div
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.8, 1, 0.8]
              }}
              transition={{ repeat: Infinity, duration: 1 }}
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: '#10b981',
                boxShadow: '0 0 8px rgba(16, 185, 129, 0.6)'
              }}
            />
          )}
          <span style={{
            fontSize: '12px',
            fontWeight: 500,
            color: 'white',
            textShadow: '0 1px 4px rgba(0, 0, 0, 0.5)',
            background: 'rgba(0, 0, 0, 0.4)',
            padding: '2px 8px',
            borderRadius: '6px',
            backdropFilter: 'blur(4px)'
          }}>
            {name.split(' ')[0]}
          </span>
        </div>
      </motion.div>
    );
  };

  // Build header text
  const headerText = useMemo(() => {
    if (friendParticipants.length === 0) return 'Waiting for friends...';
    if (friendParticipants.length === 1) return getFriendDisplayName(friendParticipants[0]);

    const names = friendParticipants.slice(0, 3).map(p => getFriendDisplayName(p).split(' ')[0]);
    if (friendParticipants.length > 3) {
      return `${names.join(', ')} +${friendParticipants.length - 3}`;
    }
    return names.join(', ');
  }, [friendParticipants]);

  // Check if any friend is speaking
  const anyFriendSpeaking = friendParticipants.some(p => p?.isSpeaking);

  const panelContent = (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 20, scale: 0.95 }}
      transition={{ type: 'spring', damping: 25, stiffness: 300 }}
      style={{
        position: 'fixed',
        bottom: isMinimized ? '24px' : '100px',
        right: '24px',
        width: getPanelWidth(),
        maxWidth: 'calc(100vw - 48px)',
        background: 'rgba(28, 28, 30, 0.95)',
        backdropFilter: 'blur(40px) saturate(180%)',
        borderRadius: '20px',
        boxShadow: '0 12px 48px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(66, 144, 158, 0.15)',
        border: anyFriendSpeaking ? '2px solid #10b981' : '2px solid rgba(66, 144, 158, 0.2)',
        overflow: 'hidden',
        zIndex: 1000,
        transition: 'width 0.3s ease, bottom 0.3s ease, border-color 0.3s ease'
      }}
    >
      {/* Header - Discord-style dark */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 16px',
        borderBottom: isMinimized ? 'none' : '1px solid rgba(255, 255, 255, 0.08)',
        background: 'rgba(35, 35, 40, 0.9)'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          flex: 1,
          minWidth: 0
        }}>
          {/* Connection status indicator */}
          <div style={{
            width: '10px',
            height: '10px',
            borderRadius: '50%',
            flexShrink: 0,
            background: friendParticipants.length > 0
              ? (connectionQuality === 'good' ? '#10b981' : connectionQuality === 'poor' ? '#f59e0b' : '#ef4444')
              : '#6b7280',
            boxShadow: friendParticipants.length > 0
              ? `0 0 12px ${connectionQuality === 'good' ? 'rgba(16, 185, 129, 0.6)' : connectionQuality === 'poor' ? 'rgba(245, 158, 11, 0.6)' : 'rgba(239, 68, 68, 0.6)'}`
              : 'none',
            animation: anyFriendSpeaking ? 'pulse 1.5s ease-in-out infinite' : 'none'
          }} />

          {/* Friend count badge */}
          {friendParticipants.length > 1 && (
            <div style={{
              background: 'rgba(66, 144, 158, 0.3)',
              color: '#6DB4CD',
              fontSize: '11px',
              fontWeight: 600,
              padding: '2px 6px',
              borderRadius: '6px',
              flexShrink: 0
            }}>
              {friendParticipants.length}
            </div>
          )}

          <span style={{
            fontSize: '14px',
            fontWeight: 600,
            color: 'white',
            letterSpacing: '-0.01em',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {headerText}
          </span>
        </div>

        <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
          {/* Minimize/Expand button */}
          <button
            onClick={() => {
              const newMinimizedState = !isMinimized;
              setIsMinimized(newMinimizedState);
              if (!newMinimizedState) {
                setUserManuallyExpanded(true);
                setTimeout(() => setUserManuallyExpanded(false), 31000);
              }
            }}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#9ca3af',
              borderRadius: '8px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              e.currentTarget.style.color = 'white';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = '#9ca3af';
            }}
            title={isMinimized ? 'Expand' : 'Minimize'}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              {isMinimized ? <polyline points="18 15 12 9 6 15" /> : <polyline points="6 9 12 15 18 9" />}
            </svg>
          </button>

          {/* Close button */}
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#9ca3af',
              borderRadius: '8px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)';
              e.currentTarget.style.color = '#ef4444';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = '#9ca3af';
            }}
            title="Close"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>

      {/* Video Content - only show when not minimized */}
      <AnimatePresence>
        {!isMinimized && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            style={{ padding: '14px', position: 'relative' }}
          >
            {/* Screen Share Spotlight Mode */}
            {activeScreenShare && (
              <div style={{ marginBottom: '12px' }}>
                <div style={{
                  borderRadius: '14px',
                  overflow: 'hidden',
                  background: '#1a1a1e',
                  position: 'relative',
                  aspectRatio: '16/9',
                  border: '2px solid rgba(66, 144, 158, 0.3)'
                }}>
                  <VideoTrack
                    key={`screen-${activeScreenShare.track.publication?.trackSid || 'share'}`}
                    trackRef={activeScreenShare.track}
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  />
                  <div style={{
                    position: 'absolute',
                    top: '8px',
                    left: '8px',
                    background: 'rgba(66, 144, 158, 0.9)',
                    color: 'white',
                    fontSize: '11px',
                    fontWeight: 600,
                    padding: '4px 8px',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                      <line x1="8" y1="21" x2="16" y2="21" />
                      <line x1="12" y1="17" x2="12" y2="21" />
                    </svg>
                    {getFriendDisplayName(activeScreenShare.participant).split(' ')[0]}'s screen
                  </div>
                </div>

                {/* Small participant tiles when screen sharing */}
                {friendParticipants.length > 0 && (
                  <div style={{
                    display: 'flex',
                    gap: '8px',
                    marginTop: '8px',
                    overflowX: 'auto',
                    paddingBottom: '4px'
                  }}>
                    {friendParticipants.map(participant => (
                      <div key={participant.sid} style={{ width: '80px', flexShrink: 0 }}>
                        {renderParticipantTile(participant, false)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Normal Grid Mode (no screen share) */}
            {!activeScreenShare && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${gridLayout.columns}, 1fr)`,
                gap: '10px'
              }}>
                {friendParticipants.map(participant =>
                  renderParticipantTile(participant, friendParticipants.length === 1)
                )}
              </div>
            )}

            {/* Empty state */}
            {friendParticipants.length === 0 && (
              <div style={{
                padding: '32px',
                textAlign: 'center',
                color: '#9ca3af'
              }}>
                <div style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '50%',
                  background: 'rgba(66, 144, 158, 0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 12px'
                }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#6DB4CD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                </div>
                <p style={{ fontSize: '14px', margin: 0 }}>Waiting for friends to join...</p>
              </div>
            )}

            {/* Student's video PiP - Tap to move corners */}
            {myVideoTrack && isCameraEnabled && (
              <motion.div
                key={`pip-container-${myVideoTrack.publication?.trackSid || 'local'}`}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                onClick={cyclePipPosition}
                style={getPipStyle()}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                title="Tap to move"
              >
                <VideoTrack
                  key={`local-${myVideoTrack.publication?.trackSid || 'video'}`}
                  trackRef={myVideoTrack}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
                <div style={{
                  position: 'absolute',
                  bottom: '4px',
                  left: '4px',
                  fontSize: '9px',
                  fontWeight: 500,
                  color: 'white',
                  background: 'rgba(0, 0, 0, 0.5)',
                  padding: '1px 4px',
                  borderRadius: '4px'
                }}>
                  You
                </div>
              </motion.div>
            )}

            {/* Video controls - Discord style */}
            <div style={{
              marginTop: '12px',
              display: 'flex',
              gap: '10px',
              justifyContent: 'center'
            }}>
              {/* Camera toggle */}
              <button
                onClick={toggleCamera}
                style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '50%',
                  border: 'none',
                  background: isCameraEnabled
                    ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                    : 'rgba(255, 255, 255, 0.1)',
                  color: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: isCameraEnabled
                    ? '0 4px 16px rgba(16, 185, 129, 0.3)'
                    : '0 4px 12px rgba(0, 0, 0, 0.2)',
                  transition: 'all 0.2s ease'
                }}
                title={isCameraEnabled ? 'Turn camera off' : 'Turn camera on'}
              >
                {isCameraEnabled ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="23 7 16 12 23 17 23 7" />
                    <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M16 16v1a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2m5.66 0H14a2 2 0 0 1 2 2v3.34l1 1L23 7v10" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                )}
              </button>
            </div>

            {/* Status indicator - Discord style */}
            {friendParticipants.length > 0 && (
              <div style={{
                marginTop: '10px',
                padding: '8px 12px',
                background: 'rgba(16, 185, 129, 0.15)',
                backdropFilter: 'blur(10px)',
                borderRadius: '10px',
                fontSize: '12px',
                fontWeight: 500,
                color: '#10b981',
                textAlign: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px'
              }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                <span>
                  {friendParticipants.length === 1
                    ? 'Connected - studying together'
                    : `${friendParticipants.length} friends studying together`}
                </span>
              </div>
            )}

            {/* Connection quality warning */}
            {connectionQuality !== 'good' && friendParticipants.length > 0 && (
              <div style={{
                marginTop: '8px',
                padding: '8px 12px',
                background: connectionQuality === 'poor' ? 'rgba(245, 158, 11, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                backdropFilter: 'blur(10px)',
                borderRadius: '10px',
                fontSize: '12px',
                fontWeight: 500,
                color: connectionQuality === 'poor' ? '#f59e0b' : '#ef4444',
                textAlign: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px'
              }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <span>{connectionQuality === 'poor' ? 'Unstable connection' : 'Connection lost'}</span>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Pulse animation keyframes */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
      `}</style>
    </motion.div>
  );

  // Early return if not showing
  if (!show) return null;

  // Use React Portal to render outside the hidden LiveKit container
  return createPortal(
    <>
      {panelContent}
      {showToast && toastMessage && (
        <ToastNotification
          message={toastMessage}
          type="error"
          show={showToast}
          onClose={() => {
            setShowToast(false);
            setToastMessage(null);
          }}
          duration={4000}
        />
      )}
    </>,
    document.body
  );
};

export default VideoCollaborationPanel;
